import { ConfigProvider } from "antd";
import { ReactNode, createContext, useState } from "react";
import { IntlProvider } from "react-intl";
import { ANTD_LOCALE, DEFAULT_LOCALE } from "~/constants/locale";
import { useTheme } from "~/hooks/useTheme";
import enUS from "~/languages/en-US.json";
import trUS from "~/languages/tr-TR.json";
import { getLocale } from "~/storages/language";
import { LanguageKey } from "~/types/language";

export type IntlMessageID = keyof typeof enUS;

const messages: { [key: string | LanguageKey]: typeof enUS | typeof trUS } = {
  "en-US": enUS,
  "tr-TR": trUS,
};

interface I18nContextProps {
  locale: LanguageKey;
  setLocale: React.Dispatch<React.SetStateAction<LanguageKey>>;
}

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nWrapperContext = createContext<I18nContextProps>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
});

export default function I18nWrapperProvider({ children }: I18nProviderProps) {
  const [locale, setLocale] = useState<LanguageKey>(getLocale());

  const { fontSizes } = useTheme();

  return (
    <I18nWrapperContext.Provider value={{ locale, setLocale }}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        <ConfigProvider
          locale={ANTD_LOCALE[locale]}
          theme={{
            hashed: false,
            cssVar: true,
            components: {
              Typography: {
                fontSize: fontSizes.fontSize,
                fontSizeHeading1: fontSizes.fontSizeHeading1,
                fontSizeHeading2: fontSizes.fontSizeHeading2,
                fontSizeHeading3: fontSizes.fontSizeHeading3,
                fontSizeHeading4: fontSizes.fontSizeHeading4,
                fontSizeHeading5: fontSizes.fontSizeHeading5,
                fontWeightStrong: 400,
                titleMarginTop: 0,
                titleMarginBottom: 0,
                margin: 0,
              },

              Select: {
                fontSize: fontSizes.fontSizeHeading3,
              },
            },
          }}
        >
          {children}
        </ConfigProvider>
      </IntlProvider>
    </I18nWrapperContext.Provider>
  );
}
